// SEO Audit API Client
import { api } from './api';
import type {
  SEOProject,
  SEOPage,
  CrawlJob,
  CannibalizationPair,
  DashboardMetrics,
  CreateProjectRequest,
  PageFilters,
  CrawlProgressResponse,
} from '@/shared/types';

// Helper to handle API responses
async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }
  return data.data;
}

// =====================
// Project API
// =====================

export async function getProjects(): Promise<{ projects: SEOProject[]; total: number }> {
  const response = await api.get('/api/seo/projects');
  return handleResponse(response);
}

export async function getProject(id: string): Promise<SEOProject> {
  const response = await api.get(`/api/seo/projects/${id}`);
  return handleResponse(response);
}

export async function createProject(data: CreateProjectRequest): Promise<SEOProject> {
  const response = await api.post('/api/seo/projects', data);
  return handleResponse(response);
}

export async function deleteProject(id: string): Promise<void> {
  const response = await api.delete(`/api/seo/projects/${id}`);
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to delete project');
  }
}

// =====================
// Crawl API
// =====================

export async function startCrawl(projectId: string): Promise<CrawlJob> {
  const response = await api.post(`/api/seo/projects/${projectId}/crawl`);
  return handleResponse(response);
}

export async function getCrawlProgress(projectId: string): Promise<CrawlProgressResponse> {
  const response = await api.get(`/api/seo/projects/${projectId}/crawl/progress`);
  return handleResponse(response);
}

export async function pauseCrawl(projectId: string): Promise<void> {
  const response = await api.post(`/api/seo/projects/${projectId}/crawl/pause`);
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to pause crawl');
  }
}

export async function resumeCrawl(projectId: string): Promise<void> {
  const response = await api.post(`/api/seo/projects/${projectId}/crawl/resume`);
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to resume crawl');
  }
}

export async function cancelCrawl(projectId: string): Promise<void> {
  const response = await api.post(`/api/seo/projects/${projectId}/crawl/cancel`);
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to cancel crawl');
  }
}

// =====================
// Pages API
// =====================

export async function getPages(
  projectId: string,
  filters?: PageFilters
): Promise<{ pages: SEOPage[]; total: number; page: number; pageSize: number }> {
  const params: Record<string, string> = {};

  if (filters) {
    if (filters.search) params.search = filters.search;
    if (filters.minScore !== undefined) params.minScore = String(filters.minScore);
    if (filters.maxScore !== undefined) params.maxScore = String(filters.maxScore);
    if (filters.hasIssues !== undefined) params.hasIssues = String(filters.hasIssues);
    if (filters.sortBy) params.sortBy = filters.sortBy;
    if (filters.sortOrder) params.sortOrder = filters.sortOrder;
    if (filters.page !== undefined) params.page = String(filters.page);
    if (filters.pageSize !== undefined) params.pageSize = String(filters.pageSize);
  }

  const response = await api.get(`/api/seo/projects/${projectId}/pages`, { params });
  return handleResponse(response);
}

export async function getPage(projectId: string, pageId: string): Promise<SEOPage> {
  const response = await api.get(`/api/seo/projects/${projectId}/pages/${pageId}`);
  return handleResponse(response);
}

// =====================
// Metrics API
// =====================

export async function getMetrics(projectId: string): Promise<DashboardMetrics> {
  const response = await api.get(`/api/seo/projects/${projectId}/metrics`);
  return handleResponse(response);
}

// =====================
// Cannibalization API
// =====================

export async function getCannibalization(projectId: string): Promise<CannibalizationPair[]> {
  const response = await api.get(`/api/seo/projects/${projectId}/cannibalization`);
  return handleResponse(response);
}

export async function getSimilarityMatrix(
  projectId: string,
  limit?: number
): Promise<{ pages: { id: string; url: string; title?: string }[]; matrix: number[][] }> {
  const params: Record<string, string> = {};
  if (limit) params.limit = String(limit);

  const response = await api.get(`/api/seo/projects/${projectId}/cannibalization/matrix`, { params });
  return handleResponse(response);
}

export async function getKeywordClusters(
  projectId: string
): Promise<{ keyword: string; pages: { id: string; url: string; density: number }[] }[]> {
  const response = await api.get(`/api/seo/projects/${projectId}/keyword-clusters`);
  return handleResponse(response);
}

// =====================
// Export API
// =====================

export function getExportUrl(projectId: string, format: 'json' | 'csv'): string {
  const baseUrl = (import.meta as unknown as { env: { VITE_API_URL?: string } }).env.VITE_API_URL || '';
  return `${baseUrl}/api/seo/projects/${projectId}/export?format=${format}`;
}

export async function exportProject(projectId: string, format: 'json' | 'csv'): Promise<Blob> {
  const response = await api.get(`/api/seo/projects/${projectId}/export`, {
    params: { format },
  });

  if (!response.ok) {
    throw new Error('Failed to export project');
  }

  return response.blob();
}
